/**
 * useAIGeneration Hook
 * 
 * Custom hook for AI job post generation with proper error handling
 * Following SOLID principles:
 * - Single Responsibility: Handles AI generation logic only
 * - Open/Closed: Extensible through options without modification
 * - Interface Segregation: Only exposes necessary methods
 * - Dependency Inversion: Depends on service abstraction
 */

import { useState } from 'react';
import { UseAIGenerationReturn, UseAIGenerationOptions, JobDescriptionEntity } from '../../types/Step0.types';
import jobDescriptionService from '../../../../Service/job.description.service';
import { openNotification } from '../../../../Components/Notification';
import { message } from 'antd';

/**
 * useAIGeneration Hook
 * 
 * Features:
 * - AI job post generation with loading states
 * - Comprehensive error handling
 * - Success/error callbacks
 * - Data clearing utilities
 * - Type-safe return values
 */
export const useAIGeneration = (options?: UseAIGenerationOptions): UseAIGenerationReturn => {
  // State management
  const [isGenerating, setIsGenerating] = useState<boolean>(false);
  const [error, setError] = useState<string | null>(null);
  const [generatedData, setGeneratedData] = useState<JobDescriptionEntity | null>(null);

  /**
   * Clear error state
   * Utility function for error state management
   */
  const clearError = () => {
    setError(null);
  };

  /**
   * Clear generated data
   * Utility function for data state management
   */
  const clearData = () => {
    setGeneratedData(null);
  };

  /**
   * Format AI response to JobDescriptionEntity
   * Transforms API response to match frontend entity structure
   */
  const formatJobPostResponse = (response: any): JobDescriptionEntity => {
    return {
      _id: '',
      title: response.title || 'Generated Job Post',
      description: response.description || '',
      position: response.position || 'Position Title',
      requirements: (response.requirements || []).map((req: any, index: number) => ({
        id: index + 1,
        topic: req.topic || '',
        description: req.description || '',
        mustMatch: req.mustMatch ?? true,
      })),
      additionalRequirements: [],
      numberOfOpenings: 1,
      numberOfApplications: 0,
      numberOfQualifiedApplications: 0,
      numberOfPendingInterviews: 0,
      numberOfHiredApplications: 0,
      numberOfRejectedApplications: 0,
      interviewers: [],
      isActive: true,
      orgId: '',
      createdAt: new Date(),
      updatedAt: new Date(),
      createdBy: '',
      ctc: undefined,
      noticePeriod: undefined,
      endDate: '',
      isProfileMatchRequired: true,
      interviewRounds: [],
      interviewGuests: [],
      slug: '',
      interviewRoundExpiry: 5,
      // Include skills extracted from job description by AI
      skills: Array.isArray(response.skills)
        ? response.skills.filter((s: any) => typeof s === 'string' && s.trim().length > 0)
        : [],
    };
  };

  /**
   * Handle input validation errors
   * Helper function to reduce cognitive complexity
   */
  const handleValidationError = (errorMsg: string) => {
    setError(errorMsg);
    
    if (!options?.onError) {
      message.error(errorMsg);
    }
    
    if (options?.onError) {
      options.onError(errorMsg);

    }
  };

  /**
   * Handle API errors
   * Helper function to reduce cognitive complexity
   */
  const handleAPIError = (err: unknown) => {
    let errorMsg: string;
    
    if (err instanceof Error) {
      errorMsg = err.message;
    } else if (typeof err === 'string') {
      errorMsg = err;
    } else {
      errorMsg = 'An unexpected error occurred while generating the job post. Please try again.';
    }
    
    setError(errorMsg);
    
    if (!options?.onError) {
      message.error('Generation Failed: ' + errorMsg);
    }
    
    if (options?.onError) {
      options.onError(errorMsg);
    }
  };

  /**
   * Handle successful generation
   * Helper function to reduce cognitive complexity
   */
  const handleGenerationSuccess = (jobPost: JobDescriptionEntity) => {
    setGeneratedData(jobPost);
    
    if (!options?.onSuccess) {
      message.success('Job post generated successfully!');
    }
    
    if (options?.onSuccess) {
      options.onSuccess(jobPost);
    }
  };

  /**
   * Generate job post from AI prompt
   * Main function for AI generation with comprehensive error handling
   */
  const generateJobPost = async (prompt: string): Promise<JobDescriptionEntity | null> => {
    // Input validation
    if (!prompt?.trim()) {
      handleValidationError('Prompt cannot be empty. Please provide a description for the job post.');
      return null;
    }

    // Check prompt length
    if (prompt.trim().length < 10) {
      handleValidationError('Prompt is too short. Please provide a more detailed description (at least 10 characters).');
      return null;
    }

    try {
      // Set loading state
      setIsGenerating(true);
      setError(null);
      
      // Call AI service
      const response = await jobDescriptionService.generateJobDescription(prompt.trim());
      
      // Validate response
      if (!response) {
        throw new Error('No response received from AI service');
      }
      
      // Format response to entity
      const jobPost = formatJobPostResponse(response);
      
      // Handle success
      handleGenerationSuccess(jobPost);
      
      return jobPost;
      
    } catch (err) {
      // Handle API errors
      handleAPIError(err);
      return null;
      
    } finally {
      // Always clear loading state
      setIsGenerating(false);
    }
  };

  // Return hook interface
  return {
    generateJobPost,
    isGenerating,
    error,
    generatedData,
    clearError,
    clearData,
  };
};
